import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

export default function HomePage() {
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(null);
  const [showSpecialtiesMenu, setShowSpecialtiesMenu] = useState(false);
  const [currentInsuranceIndex, setCurrentInsuranceIndex] = useState(0);
  const [annonces, setAnnonces] = useState<string[]>([]);
  const insurancesPerView = 2;

  // Charger les annonces défilantes depuis la base
  useEffect(() => {
    const fetchAnnonces = async () => {
      try {
        const { data, error } = await supabase
          .from('annonces_defilantes')
          .select('message')
          .eq('actif', true)
          .order('ordre', { ascending: true })
          .order('date_creation', { ascending: true });
        if (error) throw error;
        const messages = (data || []).map((a: { message: string }) => a.message);
        if (messages.length > 0) {
          setAnnonces(messages);
        }
      } catch (err) {
        console.error('Erreur chargement des annonces:', err);
      }
    };
    fetchAnnonces();
  }, []);

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale, .stagger-children').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const specialties = [
    {
      name: 'Médecine Générale',
      icon: 'ri-stethoscope-line',
      description: 'Rencontrez nos médecins généralistes pour un diagnostic complet et des soins de santé primaires adaptés à toute la famille.'
    },
    {
      name: 'Urologie et Chirurgie Générale',
      icon: 'ri-surgical-mask-line',
      description: 'Nos spécialistes en urologie et chirurgie générale vous accompagnent avec expertise pour tous vos besoins chirurgicaux.'
    },
    {
      name: 'Maternité (Sage-femme)',
      icon: 'ri-parent-line',
      description: 'Accompagnement personnalisé de la grossesse à l\'accouchement par nos sages-femmes expérimentées : consultations prénatales, suivi post-natal et conseils en allaitement.'
    },
    {
      name: 'Gynécologie',
      icon: 'ri-women-line',
      description: 'Consultations gynécologiques, dépistages, suivi contraceptif et prise en charge des pathologies gynécologiques dans un cadre confidentiel et bienveillant.'
    },
    {
      name: 'Psychologie et Psychiatrie',
      icon: 'ri-mental-health-line',
      description: 'Prenez soin de votre santé mentale avec nos psychologues et psychiatres qualifiés pour un accompagnement personnalisé.'
    },
    {
      name: 'Pédiatrie',
      icon: 'ri-bear-smile-line',
      description: 'Des soins spécialisés pour vos enfants, de la naissance à l\'adolescence, avec des pédiatres attentionnés et expérimentés.'
    },
    {
      name: 'ORL',
      icon: 'ri-volume-up-line',
      description: 'Consultez nos spécialistes ORL pour tous les problèmes liés aux oreilles, au nez et à la gorge.'
    },
    {
      name: 'Ophtalmologie',
      icon: 'ri-eye-line',
      description: 'Protégez votre vision avec nos ophtalmologues équipés des dernières technologies de diagnostic et de traitement.'
    },
    {
      name: 'Kinésithérapie',
      icon: 'ri-walk-line',
      description: 'Retrouvez votre mobilité et soulagez vos douleurs grâce à nos kinésithérapeutes professionnels.'
    },
    {
      name: 'Traumatologie et Orthopédie',
      icon: 'ri-first-aid-kit-line',
      description: 'Traitement expert des fractures, entorses et problèmes osseux par nos spécialistes en traumatologie et orthopédie.'
    },
    {
      name: 'Hématologie',
      icon: 'ri-drop-line',
      description: 'Diagnostic et traitement des maladies du sang avec nos hématologues spécialisés.'
    },
    {
      name: 'Dermatologie',
      icon: 'ri-hand-sanitizer-line',
      description: 'Prenez soin de votre peau avec nos dermatologues pour tous vos problèmes cutanés et esthétiques.'
    },
    {
      name: 'Hépato Gastro Entérologie',
      icon: 'ri-capsule-line',
      description: 'Consultez nos gastro-entérologues pour tous les troubles digestifs et hépatiques.'
    },
    {
      name: 'Cardiologie',
      icon: 'ri-heart-pulse-line',
      description: 'Protégez votre cœur avec nos cardiologues équipés pour le diagnostic et le traitement des maladies cardiovasculaires.'
    },
    {
      name: 'Échographie Radiologie',
      icon: 'ri-scan-line',
      description: 'Imagerie médicale de pointe pour des diagnostics précis avec nos équipements d\'échographie et de radiologie modernes.'
    },
    {
      name: 'Médecine physique et Réadaptation',
      icon: 'ri-wheelchair-line',
      description: 'Programmes de réadaptation personnalisés pour retrouver votre autonomie après une blessure ou une maladie.'
    },
    {
      name: 'Pneumologie',
      icon: 'ri-lungs-line',
      description: 'Soins spécialisés pour vos poumons et votre système respiratoire par nos pneumologues experts.'
    },
    {
      name: 'Endocrinologie',
      icon: 'ri-test-tube-line',
      description: 'Traitement des troubles hormonaux et métaboliques comme le diabète avec nos endocrinologues.'
    },
    {
      name: 'Neurologie et neurochirurgie',
      icon: 'ri-brain-line',
      description: 'Prise en charge des maladies du système nerveux par nos neurologues et neurochirurgiens spécialisés.'
    },
    {
      name: 'Vaccination',
      icon: 'ri-syringe-line',
      description: 'Protégez-vous et votre famille avec nos services de vaccination pour tous les âges.'
    },
    {
      name: 'Laboratoire d\'analyses biomédicales',
      icon: 'ri-microscope-line',
      description: 'Analyses médicales complètes et fiables avec notre laboratoire équipé des dernières technologies.'
    },
  ];

  const insurances = [
    { name: 'ASCOMA' },
    { name: 'SANLAM' },
    { name: 'SUNU' },
    { name: 'GRAS-SAVOYE' },
    { name: 'CNSS' },
    { name: 'NOBILA' },
    { name: 'OLEA' },
    { name: 'IMEX PHARMA' },
    { name: 'EMMAUS' },
    { name: 'ATLANTIQUE ASSURANCE' },
    { name: 'GAB' },
  ];

  const handleSpecialtyClick = (specialtyName: string) => {
    if (selectedSpecialty === specialtyName) {
      setSelectedSpecialty(null);
    } else {
      setSelectedSpecialty(specialtyName);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentInsuranceIndex((prev) => {
        const maxIndex = Math.ceil(insurances.length / insurancesPerView) - 1;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const whyCards = [
    {
      num: '01',
      color: 'text-clinic-blue',
      bar: 'from-clinic-blue to-clinic-green',
      title: 'Équipe expérimentée',
      desc: 'Des médecins qualifiés et spécialisés dans plus de 20 domaines médicaux pour vous accompagner',
    },
    {
      num: '02',
      color: 'text-clinic-green',
      bar: 'from-clinic-green to-clinic-gold',
      title: 'Disponibilité 24h/24',
      desc: 'Ouvert tous les jours avec un service d\'urgences disponible 24h/24 pour votre sécurité',
    },
    {
      num: '03',
      color: 'text-clinic-gold',
      bar: 'from-clinic-gold to-clinic-blue',
      title: 'Équipements modernes',
      desc: 'Technologies de pointe et installations récentes pour des diagnostics précis et des soins optimaux',
    },
  ];

  const jeuxCards = [
    { name: 'Puzzle du Corps Humain', icon: 'ri-body-scan-line', color: 'bg-clinic-blue/10 text-clinic-blue' },
    { name: 'Quiz Santé', icon: 'ri-question-answer-line', color: 'bg-clinic-green/10 text-clinic-green' },
    { name: 'Memory Médical', icon: 'ri-brain-line', color: 'bg-clinic-gold/15 text-clinic-gold' },
    { name: 'Snake Nutrition', icon: 'ri-apple-line', color: 'bg-clinic-blue/10 text-clinic-blue' },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* ========== HERO SECTION ========== */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center parallax-slow"
          style={{ backgroundImage: 'url(https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/home-bg.webp)' }}
        ></div>
        <div className="absolute inset-0 hero-photo-scrim"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-xl">
            <p className="hero-subtitle text-xs md:text-sm font-bold text-clinic-green uppercase tracking-widest mb-3 md:mb-4">
              Clinique Sainte Famille · Cotonou, Bénin
            </p>
            <h1 className="hero-title text-2xl sm:text-3xl md:text-5xl font-bold mb-4 md:mb-6 leading-tight text-white">
              Des soins de qualité, pour toute la famille.
            </h1>
            <p className="hero-subtitle text-sm md:text-lg mb-6 md:mb-8 text-white/80 leading-relaxed">
              Une clinique à taille humaine, où l'on vous écoute, vous soigne et vous accompagne à chaque étape.
            </p>
            <div className="hero-cta flex flex-col sm:flex-row gap-3 md:gap-4 items-start sm:items-center">
              <Link
                to="/rendez-vous"
                className="btn-ripple btn-accent w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-lg whitespace-nowrap text-center"
              >
                Prendre rendez-vous
              </Link>
              <Link
                to="/services"
                className="btn-ripple btn-primary w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-lg whitespace-nowrap text-center"
              >
                Découvrir nos services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BANDEAU INFOS DÉFILANT ========== */}
      <section className="relative py-4 md:py-5 bg-gradient-to-r from-clinic-blue-dark via-clinic-blue to-clinic-blue-dark overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-15" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 45%), radial-gradient(circle at 85% 50%, rgba(141,201,63,0.3) 0%, transparent 45%)' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex items-center gap-4 md:gap-6">
            {/* Logo + nom clinique */}
            <div className="flex items-center gap-3 flex-shrink-0 bg-white/15 backdrop-blur-sm rounded-full p-2 pr-2 md:pl-2 md:pr-6 md:py-2 border border-white/20">
              <img
                src="https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/logo.jpeg"
                alt="Clinique Sainte Famille"
                className="h-9 w-9 md:h-11 md:w-11 rounded-full object-cover border-2 border-white/70 flex-shrink-0"
              />
              <span className="hidden md:inline text-white font-bold text-sm md:text-base whitespace-nowrap">Clinique Sainte Famille</span>
            </div>

            {/* Bande défilante */}
            {annonces.length > 0 && (
              <div className="flex-1 overflow-hidden relative">
                <div className="ticker-track items-center">
                  <div className="flex items-center whitespace-nowrap">
                    {annonces.map((msg, i) => (
                      <span key={i} className="flex items-center">
                        <span className="text-white text-sm md:text-base font-medium px-6">{msg}</span>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center whitespace-nowrap" aria-hidden="true">
                    {annonces.map((msg, i) => (
                      <span key={`dup-${i}`} className="flex items-center">
                        <span className="text-white text-sm md:text-base font-medium px-6">{msg}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ========== MISSION + POURQUOI NOUS CHOISIR ========== */}
      <section className="relative py-12 md:py-20 bg-gradient-to-b from-white via-clinic-blue/[0.02] to-white overflow-hidden">
        {/* Decorative animated lines */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
          <div className="absolute top-0 left-1/4 w-px h-full bg-clinic-blue animate-pulse" style={{ animationDuration: '4s' }}></div>
          <div className="absolute top-0 left-3/4 w-px h-full bg-clinic-blue animate-pulse" style={{ animationDelay: '2s', animationDuration: '3.5s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* Partie 1 — Mission */}
          <div className="text-center mb-12 md:mb-16 reveal">
            <div className="inline-flex items-center gap-2 bg-clinic-green/10 text-clinic-green px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-medium mb-4 md:mb-5">
              <span>Notre mission</span>
            </div>
            <h2 className="text-lg md:text-3xl lg:text-4xl font-bold text-clinic-blue-dark mb-4 md:mb-6">
              Objectif de notre plateforme
            </h2>
            <div className="h-px w-24 bg-clinic-green mx-auto mb-4 md:mb-6 line-draw"></div>
            <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
              L'application de la <strong className="text-clinic-blue-dark">Clinique Sainte Famille</strong> permet aux patients de prendre rendez-vous en ligne, consulter leurs médecins à distance par visioconférence, consulter leurs résultats d'analyses, recevoir des notifications de rappel et gérer leur dossier médical de manière sécurisée. Cette plateforme connecte les patients, les médecins, les infirmiers, les laborantins et le personnel administratif pour un suivi médical optimal et personnalisé.
            </p>
          </div>

          {/* Animated separator */}
          <div className="flex items-center justify-center mb-12 md:mb-16 reveal">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-clinic-blue/20 to-transparent max-w-xs line-draw"></div>
            <div className="mx-4 md:mx-6 w-10 h-10 bg-clinic-green/10 rounded-full flex items-center justify-center">
              <i className="ri-heart-line text-clinic-green text-base md:text-lg"></i>
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-clinic-blue/20 to-transparent max-w-xs line-draw"></div>
          </div>

          {/* Partie 2 — Pourquoi nous choisir */}
          <div className="text-center mb-8 md:mb-12 reveal">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-clinic-blue-dark mb-2 md:mb-3">
              Pourquoi nous choisir ?
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto">
              Une clinique moderne au service de votre santé et de votre bien-être
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8 stagger-children">
            {whyCards.map((card, i) => (
              <div
                key={i}
                className="group relative bg-white p-5 md:p-8 rounded-3xl border-2 border-clinic-blue/8 hover:border-clinic-green/30 card-lift cursor-default overflow-hidden"
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${card.bar}`}></div>
                <span className={`block text-4xl md:text-5xl font-bold ${card.color} opacity-20 group-hover:opacity-40 transition-opacity duration-500 leading-none mb-3 md:mb-5`}>
                  {card.num}
                </span>
                <h3 className="text-sm md:text-lg font-bold text-clinic-blue-dark mb-2 md:mb-3 group-hover:text-clinic-green transition-colors duration-500">
                  {card.title}
                </h3>
                <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ASSURANCES PARTENAIRES ========== */}
      <section className="py-10 md:py-24 bg-gradient-to-br from-clinic-blue/15 to-clinic-green/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16 reveal">
            <h2 className="text-xl md:text-4xl lg:text-5xl font-bold text-clinic-blue-dark mb-3 md:mb-6">
              Assurances partenaires
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
              Nous acceptons les principales assurances santé. N'oubliez pas d'apporter votre carte d'assurance lors de votre visite.
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 stagger-children">
            {insurances.map((insurance, index) => (
              <div
                key={index}
                className="bg-white border-2 border-clinic-blue/10 rounded-2xl p-6 md:p-8 card-lift hover:border-clinic-green/30 transition-all duration-300 cursor-pointer flex items-center justify-center"
              >
                <h3 className="text-xl font-bold text-clinic-blue-dark text-center">
                  {insurance.name}
                </h3>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${currentInsuranceIndex * 100}%)` }}
              >
                {Array.from({ length: Math.ceil(insurances.length / insurancesPerView) }).map((_, pageIndex) => (
                  <div key={pageIndex} className="w-full flex-shrink-0 px-1">
                    <div className="grid grid-cols-2 gap-3">
                      {insurances
                        .slice(pageIndex * insurancesPerView, (pageIndex + 1) * insurancesPerView)
                        .map((insurance, index) => (
                          <div
                            key={index}
                            className="bg-white border-2 border-clinic-blue/10 rounded-xl p-4 card-lift flex items-center justify-center"
                          >
                            <h3 className="text-xs font-bold text-clinic-blue-dark leading-tight text-center">
                              {insurance.name}
                            </h3>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center gap-2 mt-5">
              {Array.from({ length: Math.ceil(insurances.length / insurancesPerView) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentInsuranceIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentInsuranceIndex === index
                      ? 'bg-clinic-blue w-6'
                      : 'bg-gray-300 w-2'
                  }`}
                  aria-label={`Aller à la page ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="mt-6 md:mt-12 text-center reveal">
            <p className="text-xs md:text-base text-gray-600 bg-white/80 backdrop-blur-sm rounded-xl p-3 md:p-6 inline-block shadow-md max-w-full">
              <i className="ri-information-line text-clinic-blue mr-2"></i>
              Veuillez présenter votre carte d'assurance lors de votre rendez-vous
            </p>
          </div>
        </div>
      </section>

      {/* ========== SPECIALTIES SECTION ========== */}
      <section className="py-10 md:py-20 bg-gradient-to-b from-white to-clinic-blue/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16 reveal">
            <h2 className="text-xl md:text-4xl font-bold text-clinic-blue-dark mb-2 md:mb-4">Nos spécialités médicales</h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto">
              Plus de 20 spécialités pour répondre à tous vos besoins de santé
            </p>
          </div>

          {/* Mobile: Menu déroulant */}
          <div className="lg:hidden mb-6 reveal">
            <button
              onClick={() => setShowSpecialtiesMenu(!showSpecialtiesMenu)}
              className="w-full bg-white p-3 md:p-4 rounded-2xl shadow-lg border-2 border-clinic-blue/20 hover:border-clinic-green transition-all flex items-center justify-between card-lift"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 md:w-10 md:h-10 bg-clinic-blue/10 rounded-xl flex items-center justify-center">
                  <i className="ri-hospital-line text-lg md:text-xl text-clinic-blue"></i>
                </div>
                <span className="font-semibold text-clinic-blue-dark text-sm md:text-base">
                  {showSpecialtiesMenu ? 'Masquer les spécialités' : 'Voir toutes les spécialités'}
                </span>
              </div>
              {showSpecialtiesMenu ? (
                <i className="ri-arrow-up-s-line text-xl md:text-2xl text-clinic-blue transition-transform duration-300"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-xl md:text-2xl text-clinic-blue transition-transform duration-300"></i>
              )}
            </button>

            {showSpecialtiesMenu && (
              <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-[var(--shadow-elevated)] overflow-hidden max-h-80 md:max-h-96 overflow-y-auto">
                {specialties.map((specialty, index) => (
                  <div key={index} className="border-b border-gray-100 last:border-b-0">
                    <button
                      onClick={() => handleSpecialtyClick(specialty.name)}
                      className="w-full p-3 md:p-4 text-left hover:bg-clinic-blue/5 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 md:w-10 md:h-10 bg-clinic-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className={`${specialty.icon} text-base md:text-lg text-clinic-blue`}></i>
                        </div>
                        <span className="font-medium text-clinic-blue-dark text-xs md:text-sm">
                          {specialty.name}
                        </span>
                      </div>
                      {selectedSpecialty === specialty.name ? (
                        <i className="ri-arrow-up-s-line text-clinic-blue flex-shrink-0 ml-2 transition-transform duration-300"></i>
                      ) : (
                        <i className="ri-arrow-down-s-line text-clinic-blue flex-shrink-0 ml-2 transition-transform duration-300"></i>
                      )}
                    </button>

                    {selectedSpecialty === specialty.name && (
                      <div className="px-3 md:px-4 pb-3 md:pb-4 bg-clinic-green/5 border-t border-clinic-green/20 animate-fadeIn">
                        <p className="text-xs text-gray-700 leading-relaxed mb-2 md:mb-3 mt-2 md:mt-3">
                          {specialty.description}
                        </p>
                        <Link
                          to="/rendez-vous"
                          className="inline-block text-xs font-medium text-clinic-green hover:text-clinic-green/80 transition-colors whitespace-nowrap underline-expand"
                        >
                          Prendre rendez-vous <i className="ri-arrow-right-line ml-1"></i>
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop: Grid */}
          <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 stagger-children">
            {specialties.map((specialty, index) => (
              <div key={index}>
                <button
                  onClick={() => handleSpecialtyClick(specialty.name)}
                  className="w-full bg-white p-4 md:p-6 rounded-2xl border border-gray-100 hover:border-clinic-green/40 transition-all card-elevated cursor-pointer group text-left"
                >
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-clinic-blue/10 rounded-xl flex items-center justify-center mb-2 md:mb-4 group-hover:bg-clinic-green/10 transition-colors duration-500">
                    <i className={`${specialty.icon} icon-bounce text-lg md:text-2xl text-clinic-blue group-hover:text-clinic-green transition-colors duration-500`}></i>
                  </div>
                  <h3 className="font-semibold text-xs md:text-base text-clinic-blue-dark group-hover:text-clinic-green transition-colors duration-500">
                    {specialty.name}
                  </h3>
                  {selectedSpecialty === specialty.name ? (
                    <i className="ri-arrow-up-s-line text-clinic-blue group-hover:text-clinic-green transition-colors mt-1 md:mt-2"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-clinic-blue group-hover:text-clinic-green transition-colors mt-1 md:mt-2"></i>
                  )}
                </button>

                {selectedSpecialty === specialty.name && (
                  <div className="mt-2 md:mt-3 p-3 md:p-4 bg-clinic-green/10 border-2 border-clinic-green/30 rounded-2xl animate-fadeIn">
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                      {specialty.description}
                    </p>
                    <Link
                      to="/rendez-vous"
                      className="inline-block mt-2 md:mt-3 text-xs md:text-sm font-medium text-clinic-green hover:text-clinic-green/80 transition-colors whitespace-nowrap underline-expand"
                    >
                      Prendre rendez-vous <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA SECTION ========== */}
      <section className="py-10 md:py-20 bg-white border-t-2 border-clinic-blue/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center reveal">
          <h2 className="text-xl md:text-4xl font-bold mb-3 md:mb-6 text-clinic-blue-dark">Prêt à prendre soin de votre santé ?</h2>
          <p className="text-sm md:text-xl mb-5 md:mb-8 text-gray-600">
            Inscrivez-vous dès maintenant et prenez rendez-vous avec nos spécialistes
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
            <Link
              to="/register"
              className="btn-ripple btn-accent w-full sm:w-auto inline-block px-6 md:px-10 py-3 md:py-4 rounded-full font-semibold text-sm md:text-lg whitespace-nowrap text-center"
            >
              Créer mon compte
            </Link>
          </div>
        </div>
      </section>

      {/* ========== JEUX ÉDUCATIFS ========== */}
      <section className="py-10 md:py-16 bg-gradient-to-br from-clinic-blue/5 via-white to-clinic-green/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-12 reveal">
            <div className="inline-flex items-center gap-2 bg-clinic-green/10 text-clinic-green px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4 animate-pulse-soft">
              Nouveau !
            </div>
            <h2 className="text-xl md:text-4xl font-bold text-clinic-blue-dark mb-2 md:mb-3">Apprendre la santé en s&apos;amusant</h2>
            <p className="text-xs md:text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez nos mini-jeux éducatifs pour toute la famille : anatomie, nutrition, quiz et bien plus !
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 stagger-children">
            {jeuxCards.map((jeu, i) => (
              <Link
                key={i}
                to="/jeux"
                className="bg-white p-3 md:p-6 rounded-2xl shadow-md hover:shadow-xl transition-all border-2 border-clinic-blue/10 hover:border-clinic-green/30 card-lift group text-center cursor-pointer"
              >
                <div className={`w-10 h-10 md:w-14 md:h-14 ${jeu.color} rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 transition-all duration-500`}>
                  <i className={`${jeu.icon} icon-bounce text-lg md:text-2xl`}></i>
                </div>
                <h3 className="text-xs font-semibold text-clinic-blue-dark leading-tight group-hover:text-clinic-green transition-colors duration-300">{jeu.name}</h3>
              </Link>
            ))}
          </div>
          <div className="text-center mt-5 md:mt-8 reveal">
            <Link
              to="/jeux"
              className="btn-ripple inline-flex items-center gap-2 px-5 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-clinic-blue to-clinic-blue-dark text-white rounded-full font-semibold text-sm md:text-base hover:shadow-xl transition-all whitespace-nowrap"
            >
              Découvrir tous les jeux
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}